import type { TFunction } from 'react-i18next';
import { ScenarioSettingsLanguageVals } from 'app/components/ScenarioEditorPage/ScenarioSettings/types.ts';
import type { I18nCoreNs } from '@/i18n/dictionaries/interfaces.ts';

export interface LanguageOption {
  value: string;
  labelKey: string;
  flagCode: string;
}

export const languageOptions: LanguageOption[] = [
  { value: ScenarioSettingsLanguageVals.Norwegian, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.norwegian), flagCode: 'no-NO' },
  { value: ScenarioSettingsLanguageVals.English, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.englishus), flagCode: 'en-US' },
  { value: ScenarioSettingsLanguageVals.EnglishUk, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.englishuk), flagCode: 'en-GB' },
  { value: ScenarioSettingsLanguageVals.German, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.german), flagCode: 'de-DE' },
  { value: ScenarioSettingsLanguageVals.Dutch, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.dutch), flagCode: 'nl-NL' },
  { value: ScenarioSettingsLanguageVals.French, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.french), flagCode: 'fr-FR' },
  { value: ScenarioSettingsLanguageVals.Italian, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.italian), flagCode: 'it-IT' },
  { value: ScenarioSettingsLanguageVals.Danish, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.danish), flagCode: 'da-DK' },
  { value: ScenarioSettingsLanguageVals.Swedish, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.swedish), flagCode: 'sv-SE' },
  { value: ScenarioSettingsLanguageVals.Icelandic, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.icelandic), flagCode: 'is-IS' },
  { value: ScenarioSettingsLanguageVals.EnglishAustralia, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.englishAustralia), flagCode: 'en-AU' },
  { value: ScenarioSettingsLanguageVals.SpanishSpain, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.spanishSpain), flagCode: 'es-ES' },
  { value: ScenarioSettingsLanguageVals.SpanishMexico, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.spanishMexico), flagCode: 'es-MX' },
  { value: ScenarioSettingsLanguageVals.SpanishUS, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.spanishUs), flagCode: 'es-US' },
  { value: ScenarioSettingsLanguageVals.ArabicUae, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.arabicUae), flagCode: 'ar-AE' },
  { value: ScenarioSettingsLanguageVals.Hindi, labelKey: nameof.full<I18nCoreNs>((l) => l.languagePicker.hindi), flagCode: 'hi-IN' }
];

/**
 * Converts a language code to a user-friendly display name using i18n translations.
 * Falls back to the raw language code if no translation is found.
 * 
 * @param languageCode - The language code (e.g., 'en-US', 'nb-NO')
 * @param translateFn - The i18n translation function for Core namespace
 * @returns The translated language name or the original code if not found
 */
export const getLanguageDisplayName = (
  languageCode: string | undefined | null,
  translateFn: TFunction
): string => {
  if (!languageCode) return 'N/A';

  const option = languageOptions.find((opt) => opt.value === languageCode);
  return option ? translateFn(option.labelKey) : languageCode;
};

export const getLanguageFlagCode = (languageCode: string): string => {
  const option = languageOptions.find((opt) => opt.value === languageCode);
  return option ? option.flagCode : languageCode;
};
